import { useEffect, useState } from "react";
import { FiStar, FiX } from "react-icons/fi";
import type { CustomerReviewItem } from "../../../services/customerReviews";

type CustomerReviewPromptModalProps = {
  review: CustomerReviewItem;
  submitting: boolean;
  error: string | null;
  onSubmit: (rating: number, comment: string) => void;
  onClose: () => void;
};

function formatReviewDate(value?: string | null): string {
  if (!value) return "data do atendimento";
  try {
    return new Date(value).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "data do atendimento";
  }
}

export function CustomerReviewPromptModal({
  review,
  submitting,
  error,
  onSubmit,
  onClose,
}: CustomerReviewPromptModalProps) {
  const [rating, setRating] = useState<number>(review.rating || 0);
  const [comment, setComment] = useState(review.reviewComment || "");
  const titleId = `public-customer-review-title-${review.appointmentId}`;

  useEffect(() => {
    if (submitting) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose, submitting]);

  return (
    <div
      className="fixed inset-0 z-[130] flex items-end justify-center bg-slate-950/45 px-3 py-4 backdrop-blur-sm sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <div className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-3xl border border-slate-200 bg-white p-4 shadow-2xl sm:p-6">
        <div className="flex items-start justify-between gap-3 border-b border-slate-200 pb-4">
          <div className="min-w-0">
            <span className="inline-flex rounded-full bg-amber-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-amber-700 ring-1 ring-amber-100">
              Atendimento concluído
            </span>
            <h2 id={titleId} className="mt-2 text-xl font-bold text-slate-950">
              Avalie seu atendimento
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              {review.providerName || "Profissional"} • {review.serviceName || "Serviço"}{" "}
              • {formatReviewDate(review.scheduledAt)}
            </p>
            <p className="mt-3 text-xs leading-5 text-slate-500">
              Escolha uma nota em estrelas. O comentário é opcional.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="inline-flex h-11 w-11 flex-none items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus-visible:ring-4 focus-visible:ring-primary-100 disabled:cursor-not-allowed disabled:opacity-60"
            aria-label="Fechar avaliação"
          >
            <FiX className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <div className="space-y-5 pt-5">
          <fieldset>
            <legend className="text-sm font-semibold text-slate-700">
              Quantas estrelas você dá para esse atendimento?
            </legend>
            <div className="mt-3 flex items-center gap-2" role="radiogroup">
              {[1, 2, 3, 4, 5].map((value) => {
                const selected = rating >= value;
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setRating(value)}
                    className={`inline-flex h-11 w-11 items-center justify-center rounded-xl border text-xl transition focus:outline-none focus-visible:ring-4 focus-visible:ring-amber-100 ${
                      selected
                        ? "border-amber-300 bg-amber-50 text-amber-500"
                        : "border-slate-200 bg-white text-slate-300 hover:border-amber-200 hover:text-amber-400"
                    }`}
                    role="radio"
                    aria-checked={rating === value}
                    aria-label={`${value} estrela${value === 1 ? "" : "s"}`}
                  >
                    <FiStar aria-hidden="true" />
                  </button>
                );
              })}
            </div>
          </fieldset>

          <label className="grid gap-2 text-sm">
            <span className="font-semibold text-slate-700">
              Conte como foi, se quiser
            </span>
            <textarea
              value={comment}
              onChange={(event) => setComment(event.target.value.slice(0, 2000))}
              rows={4}
              className="min-h-[7rem] w-full resize-none rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 outline-none transition focus:border-primary-300 focus:ring-4 focus:ring-primary-100"
              placeholder="Ex.: atendimento pontual, profissional cuidadoso..."
            />
            <span className="text-xs text-slate-500">{comment.length}/2000</span>
          </label>

          {error ? (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700">
              {error}
            </div>
          ) : null}

          <div className="flex flex-col gap-2 border-t border-slate-200 pt-4 sm:items-end">
            <button
              type="button"
              onClick={() => onSubmit(rating, comment)}
              disabled={submitting || rating < 1}
              className="inline-flex h-11 w-full items-center justify-center rounded-xl bg-primary-600 px-5 text-sm font-bold text-white shadow-sm shadow-primary-600/20 transition hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
            >
              {submitting ? "Enviando..." : "Enviar avaliação"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
